import React from "react";
import { ToolbarMoreButton } from "./FluentToolbarMoreButton";
import {
  Accessibility24Filled,
  Camera24Filled,
  Chat24Filled,
  DocumentArrowUpFilled,
  DrawImageFilled,
  Emoji24Filled,
  HandLeftFilled,
  HandRightOffFilled,
  ImageAltTextFilled,
  ImageCopy24Filled,
  Pen24Filled,
  People24Filled,
  QuestionCircle24Filled,
  Settings24Filled,
  ShareScreenStart24Filled,
  ShareScreenStop24Filled,
  VideoClipOptimize24Filled
} from "@fluentui/react-icons"; // Example icons
import PropTypes from "prop-types";
import configs from "../../utils/configs";
import { useSettingsContext } from "../room/SettingsContext";
import { FormattedMessage } from "react-intl";
import { useToolbarContext } from "../contexts/ToolbarContext";
import { useSocialContext } from "../contexts/SocialContext";
import { usePeopleList } from "../room/usePeopleList";
import { useShare } from "../room/useShare";
import configManager from "../../singleton/ConfigManager";
import { Badge, makeStyles } from "@fluentui/react-components";
import { useSidebarContext } from "../SidebarContext";
import { usePresence, useToggleRaisedHand } from "./FluentToolbarRaiseHandButtonContainer";
import { MediaDevices } from "../../utils/media-devices-utils";

const useStyles = makeStyles({
  active: {
    backgroundColor: "var(--primaryColorBackground)"
  }
});

export function FluentToolbarMoreButtonContainer({
  styles,
  scene,
  hubChannel,
  presences,
  mySessionId,
  mediaSearchStore,
  initialPresence,
  store,
  toggleStreamerMode
}) {
  const { onOpenChange: onSettingsOpenChange } = useSettingsContext();
  const { onEmojisOpenChange, canSpawnEmojis } = useToolbarContext();
  const { privateUnreadMessages } = useSocialContext();
  const { toggleParticipants, toggleChat } = useSocialContext();
  const { setFileUploadOpen, onDigitalArtChange, onDigitalTwinArtChange } = useToolbarContext();

  const people = usePeopleList(presences, mySessionId);
  const { canShareScreen, toggleShareScreen, sharingSource } = useShare(scene, hubChannel);
  const classes = useStyles();

  const isSharingScreen = sharingSource && sharingSource === MediaDevices.SCREEN;
  const disabled = sharingSource && sharingSource !== MediaDevices.SCREEN;
  const canSpawnCamera = !!configManager.config.spawnCamera.enabled && hubChannel.can("spawn_camera");

  const textChatEnabled = !!configManager.config.textChat?.enabled;
  const canSpawnObjects = !!configManager.config.spawnAndMoveMedia.enabled && hubChannel.can("spawn_and_move_media");
  const emojiEnabled = !!configManager.config.spawnEmoji.enabled && hubChannel.can("spawn_emoji");

  const { setSidebar } = useSidebarContext();

  const handleAvatarClick = () => {
    mediaSearchStore.sourceNavigate("avatars");
    setSidebar("avatar");
  };

  const canSpawnDrawn = hubChannel.can("spawn_drawing");

  const fileUploadOpenMenu = () => {
    setFileUploadOpen(true);
    onDigitalTwinArtChange(false);
    onDigitalArtChange(false);
  };

  const openDigitalArt = () => {
    onDigitalArtChange(true);
    setFileUploadOpen(true);
    onDigitalTwinArtChange(false);
  };

  const openDigitalTwinArt = () => {
    onDigitalArtChange(false);
    onDigitalTwinArtChange(true);
    setFileUploadOpen(true);
  };

  const presence = usePresence(scene, initialPresence);
  const onToggleHandRaised = useToggleRaisedHand(presence, hubChannel, store);

  const artworkSubMenuItems = [
    {
      id: "file",
      label: <FormattedMessage id="upload.file" defaultMessage="File" />,
      icon: <DocumentArrowUpFilled />, // Replace with appropriate icon
      onClick: fileUploadOpenMenu
    },
    {
      id: "digitalArt",
      label: <FormattedMessage id="upload.digitalArt" defaultMessage="Digital Art" />,
      icon: <ImageAltTextFilled />, // Replace with appropriate icon
      onClick: openDigitalArt
    },
    {
      id: "digitalTwinArt",
      label: <FormattedMessage id="upload.digitalTwinArt" defaultMessage="Digital Twin Art" />,
      icon: <DrawImageFilled />, // Replace with appropriate icon
      onClick: openDigitalTwinArt
    }
  ];

  const menuItems = [
    {
      title: <FormattedMessage id="interaction-group" defaultMessage="Interaction" />,
      items: [
        canSpawnObjects && {
          id: "addAvatar",
          label: <FormattedMessage id="more-menu.avatar" defaultMessage="Avatar" />,
          icon: <Accessibility24Filled />,
          onClick: handleAvatarClick
        },
        canSpawnDrawn
          ? {
              id: "pen",
              label: <FormattedMessage id="more-menu.pen" defaultMessage="Pen" />,
              icon: <Pen24Filled />,
              onClick: () => scene.emit("penButtonPressed")
            }
          : undefined,
        canSpawnCamera
          ? {
              id: "camera",
              label: <FormattedMessage id="more-menu.camera" defaultMessage="Selfie" />,
              icon: <Camera24Filled />,
              onClick: () => scene.emit("action_toggle_camera")
            }
          : undefined,
        canSpawnEmojis && emojiEnabled
          ? {
              id: "emojis",
              label: <FormattedMessage id="reaction-popover.title" defaultMessage="Emojis" />,
              icon: <Emoji24Filled />,
              onClick: () => onEmojisOpenChange(true)
            }
          : undefined,
        canShareScreen && canSpawnObjects
          ? {
              id: "shareScreen",
              label: isSharingScreen ? (
                <FormattedMessage id="room-settings-sidebar.stop-screen-sharing" defaultMessage="Stop Screen Sharing" />
              ) : (
                <FormattedMessage id="room-settings-sidebar.share-screen" defaultMessage="Share Screen" />
              ),
              icon: isSharingScreen ? <ShareScreenStop24Filled /> : <ShareScreenStart24Filled />,
              onClick: toggleShareScreen,
              disabled,
              className: isSharingScreen ? classes.active : undefined
            }
          : undefined,
        // Add Raised Hand button here if needed
        canSpawnEmojis && emojiEnabled
          ? {
              id: "Raised Hand",
              label: presence?.hand_raised ? (
                <FormattedMessage id="toolbar.lower-hand" defaultMessage="Lower Hand" />
              ) : (
                <FormattedMessage id="toolbar.raise-hand" defaultMessage="Raise Hand" />
              ),
              icon: presence?.hand_raised ? <HandRightOffFilled /> : <HandLeftFilled />,
              onClick: onToggleHandRaised,
              className: presence?.hand_raised ? classes.active : undefined
            }
          : undefined,
        canSpawnObjects && {
          id: "Lobby Cast",
          label: <FormattedMessage id="lobby-cast" defaultMessage="Lobby Cast" />,
          icon: <VideoClipOptimize24Filled />,
          onClick: toggleStreamerMode
        }
      ].filter(Boolean) // Remove undefined items
    },
    {
      title: <FormattedMessage id="collaboration-group" defaultMessage="Collaboration" />,
      items: [
        textChatEnabled && {
          id: "chat",
          label: <FormattedMessage id="chat-toolbar-button" defaultMessage="Chat" />,
          icon: <Chat24Filled />,
          onClick: toggleChat
        },
        {
          id: "participants",
          label: (
            <>
              <FormattedMessage
                id="people-sidebar.title"
                defaultMessage="Participants ({numPeople})"
                values={{ numPeople: people.length }}
              />
              {privateUnreadMessages != false && (
                <Badge size="small" style={{ position: "absolute", right: "var(--spacingVerticalSNudge)" }} />
              )}
            </>
          ),
          icon: <People24Filled />,
          onClick: toggleParticipants
        }
      ].filter(Boolean)
    },
    {
      title: <FormattedMessage id="resources-group" defaultMessage="Resources" />,
      items: [
        canSpawnObjects && {
          id: "upload",
          label: <FormattedMessage id="place-popover.item-type.upload" defaultMessage="Upload" />,
          icon: <DocumentArrowUpFilled />,
          submenu: artworkSubMenuItems
        },
        canSpawnObjects && {
          id: "assets",
          label: <FormattedMessage id="more-menu.assets" defaultMessage="Assets" />,
          icon: <ImageCopy24Filled />,
          onClick: () => setSidebar("assets")
        }
      ].filter(Boolean)
    },
    {
      title: <FormattedMessage id="metaverse-group" defaultMessage="Metaverse" />,
      items: [
        {
          id: "settings",
          label: <FormattedMessage id="settings" defaultMessage="Settings" />, // Wrapped in FormattedMessage for consistency
          icon: <Settings24Filled />,
          onClick: () => onSettingsOpenChange(true)
        },
        {
          id: "help",
          label: <FormattedMessage id="help" defaultMessage="Help" />, // Wrapped in FormattedMessage for consistency
          icon: <QuestionCircle24Filled />,
          href: configs.DOCS_URL,
          target: "_blank"
        }
      ].filter(Boolean)
    }
  ].filter(group => group.items.length > 0); // Remove empty groups

  return <ToolbarMoreButton styles={styles} items={menuItems} />;
}

export default FluentToolbarMoreButtonContainer;

FluentToolbarMoreButtonContainer.propTypes = {
  styles: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  scene: PropTypes.object.isRequired,
  hubChannel: PropTypes.object.isRequired,
  hub: PropTypes.object.isRequired,
  signedIn: PropTypes.bool.isRequired,
  presences: PropTypes.object.isRequired,
  mySessionId: PropTypes.string.isRequired,
  mediaSearchStore: PropTypes.object.isRequired,
  initialPresence: PropTypes.object,
  toggleStreamerMode: PropTypes.func
};
